import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { Pill } from '../../ui/DataBits';
import { errorText, fmtDate, rpc, type AppError } from '../admin/api';
import { loc, type Loc } from '../../lib/loc';

// Panels for School results (0019): early signals (J9) and THL alignment
// (J5), and the calendar event list shared with the calendar pages.

export type CalEvent = { id: string; kind: 'exam_week' | 'holiday' | 'event' | 'other'; title: string; starts_on: string; ends_on: string; grades: string[] | null; grade_ids: string[] | null };

export function EventList({ events, locale, onEdit, onDelete }: { events: CalEvent[]; locale: string; onEdit?: (e: CalEvent) => void; onDelete?: (e: CalEvent) => void }) {
  const { t } = useI18n();
  if (events.length === 0) return <p className="text-muted">{t['r3.cal.none']}</p>;
  return (
    <ul className="sched-list">
      {events.map((x) => (
        <li key={x.id}>
          <span>
            <strong>{x.title}</strong> <Pill kind="neutral">{t[`r3.cal.k.${x.kind}` as 'r3.cal.k.event']}</Pill>
            <span className="text-muted"> · {fmtDate(locale, x.starts_on)}{x.ends_on !== x.starts_on && <>–{fmtDate(locale, x.ends_on)}</>}
              {' · '}{x.grades ? x.grades.map((g) => t['h2.grade'](g)).join(', ') : t['r3.cal.wholeSchool']}</span>
          </span>
          {(onEdit || onDelete) && (
            <span className="row-actions">
              {onEdit && <button type="button" className="link-btn" onClick={() => onEdit(x)}>{t['r3.cal.editBtn']}<span className="visually-hidden"> – {x.title}</span></button>}
              {onDelete && <button type="button" className="link-btn" onClick={() => onDelete(x)}>{t['r3.cal.deleteBtn']}<span className="visually-hidden"> – {x.title}</span></button>}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

// ── J9 Early signals and J5 THL (panels on School results) ─────────────────

type Signal = { topic: string; shares: number[]; from: string; to: string };

export function SignalsPanel() {
  const { t, locale } = useI18n();
  const [d, setD] = useState<{ signals: Signal[]; events: CalEvent[]; scope_grades: string[] | null } | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    // B15: check the premium option first instead of calling a function that
    // can only answer "premium required".
    rpc<{ insights_premium: boolean }>('school_premium').then((p) => {
      if (!p.error && !p.data.insights_premium) return setErr({ code: 'premium_required', message: 'premium_required' });
      rpc<NonNullable<typeof d>>('head_signals').then((r) => (r.error ? setErr(r.error) : setD(r.data)));
    });
  }, []);
  if (err?.code === 'feature_off') return null;
  return (
    <section className="panel p5" aria-labelledby="sig-t">
      <h2 className="panel-title" id="sig-t">{t['r3.sig.title']}</h2>
      {err ? (
        <p className="text-muted">{err.code === 'premium_required' ? t['r3.premiumSignals'] : errorText(t, err)}</p>
      ) : !d ? (
        <p role="status">{t.loading}</p>
      ) : d.signals.length === 0 ? (
        <p className="text-muted">{t['r3.sig.none']}</p>
      ) : (
        <ul className="sched-list">
          {d.signals.map((s) => (
            <li key={s.topic}>
              <span><strong>{t[`topic.${s.topic}` as 'topic.exams']}</strong> — {t['r3.sig.rose'](s.shares.join(' → '))}</span>
            </li>
          ))}
        </ul>
      )}
      {d && d.events.length > 0 && (
        <>
          <h3 className="overline">{t['r3.sig.events']}</h3>
          <EventList events={d.events} locale={locale} />
        </>
      )}
      <p className="form-hint">{t['r3.sig.how']}</p>
    </section>
  );
}

type Thl = { key: string; topic: string; label: Loc; note: Loc; is_draft: boolean; values: { level: string; code: string; stage: string; year: number; value_pct: number; source: string }[] };

export function ThlPanel() {
  const { t, lang, num } = useI18n();
  const [d, setD] = useState<Thl[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    rpc<{ indicators: Thl[] }>('thl_alignment').then((r) => (r.error ? setErr(r.error) : setD(r.data.indicators)));
  }, []);
  if (err?.code === 'feature_off') return null;
  return (
    <section className="panel p5" aria-labelledby="thl-t">
      <h2 className="panel-title" id="thl-t">{t['r3.thl.title']} <Pill kind="warn">{t['ts.draft']}</Pill></h2>
      <p className="form-hint">{t['r3.thl.intro']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      {d && (
        <dl className="kv">
          {d.map((i) => (
            <div key={i.key}>
              <dt>{i.topic === 'mood' ? t['r3.thl.mood'] : t[`topic.${i.topic}` as 'topic.exams']}</dt>
              <dd>
                {loc(i.label, lang)}
                {i.values.length === 0 ? (
                  <span className="text-muted"> · {t['r3.thl.noValue']}</span>
                ) : (
                  i.values.slice(0, 3).map((v) => (
                    <span key={`${v.level}${v.code}${v.stage}${v.year}`} className="text-muted"> · {v.level === 'country' ? t['r3.ww.country'] : v.code} {v.stage} ({v.year}): {num(v.value_pct)} %</span>
                  ))
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="form-hint">{t['r3.thl.note']}</p>
    </section>
  );
}
